import React, { useState, useEffect, useRef } from "react";
import debounce from "lodash/debounce";
import { Col, Row } from "react-bootstrap";
import { number, string } from "prop-types";
import exceededCheckCircle from "resources/images/dynamic_score_reports/skills/score-report/check-circle.png";
import currentCheckCircle from "resources/images/dynamic_score_reports/skills/score-report/hollow-check-circle.svg";
import {
  getAverageScoreScaleCellClassName,
  getAverageScoreScaleBrackets,
} from "./helpers";

const AverageScoreScale = ({ score, styleClass = "", testId }) => {
  const [avgScaleWidth, setAvgScaleWidth] = useState(null);

  const avgScale = useRef(null);

  useEffect(() => {
    function handleResize() {
      // Set window width/height to state
      if (avgScale.current) {
        setAvgScaleWidth(
          Math.round(avgScale.current.getBoundingClientRect().width)
        );
      }
    }

    // Add event listener
    window.addEventListener("resize", debounce(handleResize, 150));

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", () => handleResize);
  }, []);

  const averageScoreScaleBrackets = getAverageScoreScaleBrackets(testId);

  // Proficiency tests display proficiency checkmark icons next to scale brackets
  const getLevelIcon = (labelIndex) => {
    if (!isProficiencyTest) {
      return null;
    }

    const levelMin = 4 * labelIndex + 1;
    const levelMax = 4 * (labelIndex + 1);

    if (score >= levelMin && score <= levelMax) {
      return (
        <img
          src={currentCheckCircle}
          alt="Current level"
          width="24"
          height="24"
          className="currentCheckCircle"
        />
      );
    }
    if (score > levelMax) {
      return (
        <img
          src={exceededCheckCircle}
          width="20"
          height="20"
          alt="Exceeded level"
          className="exceededCheckCircle"
        />
      );
    }

    return null;
  };

  const getScoreBubbleLeft = () => {
    const maxScore = isProficiencyTest ? 20 : 100;

    let left = Math.round((Number(score) / maxScore) * avgScaleWidth);

    if (isProficiencyTest) {
      left -= 26;
    }

    return `${left}px`;
  };

  const isProficiencyTest = [53, 54, 55, 57].includes(testId);
  const isCTRSETest = testId === 59;

  return (
    <Col className={`average-score-scale-wrapper ${styleClass}`}>
      {styleClass === "candidate-summary" && !isCTRSETest &&(
        <Row className="justify-content-center">
          <strong style={{ fontSize: "16px", color: "#011f4e" }}>
            Overall Score
          </strong>
        </Row>
      )}
      <Row className="d-block" ref={avgScale}>
        <ul className="score-scale-row">
          {Array.from({ length: 20 }).map((_, i) => (
            <li
              key={i} // eslint-disable-line react/no-array-index-key
              style={{ width: "5%" }}
              className={`scale-cell-${getAverageScoreScaleCellClassName(
                testId,
                i
              )}`}
            />
          ))}
        </ul>
      </Row>
      <Row>
        {Object.keys(averageScoreScaleBrackets).map((label, i) => (
          <div
            key={label}
            style={{ width: `${averageScoreScaleBrackets[label]}%` }}
          >
            <div className="scale-bracket">&nbsp;</div>
            <p className={`scale-label ${styleClass}`}>
              {getLevelIcon(i)}
              {label}
            </p>
          </div>
        ))}
      </Row>
      {String(score) && avgScaleWidth ? (
        <div
          className={`scale-score-bubble ${styleClass}`}
          style={{
            left: getScoreBubbleLeft(),
            top: isProficiencyTest ? "-47px" : "-64px",
          }}
        >
          <strong>{score}</strong>
        </div>
      ) : null}
    </Col>
  );
};

AverageScoreScale.propTypes = {
  score: number,
  styleClass: string,
  testId: number,
};

export default AverageScoreScale;
